import type { ReadyToSharePlatform } from '@change-corgi/middle-end-gql-schema';

import type { FullPostType } from 'src/app/pages/starterDashboardV2/shared/components';
import type { PlatformDataMap } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { ReadyToSharePostItem, ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

export type PlatformType = 'facebook' | 'reddit' | 'instagram' | 'facebookgroups';

export type RecommendationsData = {
	hasFacebookGroups: boolean;
	hasRedditGroups: boolean;
	isLoading: boolean;
};

// Map frontend platform types to GraphQL platform enum values
export const PLATFORM_TO_GQL_PLATFORM: Record<PlatformType, ReadyToSharePlatform> = {
	facebook: 'FACEBOOK',
	facebookgroups: 'FACEBOOKGROUPS',
	instagram: 'INSTAGRAM',
	reddit: 'REDDIT',
};

export type PlatformGroupedPosts = {
	facebook: ReadyToSharePostWithPlatform[];
	reddit: ReadyToSharePostWithPlatform[];
	instagram: ReadyToSharePostWithPlatform[];
	facebookgroups: ReadyToSharePostWithPlatform[];
};

export const ALL_AVAILABLE_PLATFORMS: PlatformType[] = ['facebook', 'reddit', 'instagram', 'facebookgroups'];

export const PLATFORM_LABELS: Record<PlatformType, string> = {
	facebook: 'Facebook',
	reddit: 'Reddit',
	instagram: 'Instagram',
	facebookgroups: 'Facebook Groups',
};

/**
 * Groups ready-to-share posts by platform
 * @param posts - Array of posts from the API
 * @returns Object with posts grouped by platform
 */
export function groupPostsByPlatform(posts: ReadyToSharePostWithPlatform[]): PlatformGroupedPosts {
	const grouped: PlatformGroupedPosts = {
		facebook: [],
		reddit: [],
		instagram: [],
		facebookgroups: [],
	};

	posts.forEach((post) => {
		const platformKey = post.platform.toLowerCase();
		const keyAsType = platformKey as PlatformType;
		if (platformKey in grouped) {
			grouped[keyAsType].push(post);
		}
	});

	return grouped;
}

/**
 * Gets posts for a specific platform
 * @param posts - Array of posts from the API
 * @param platform - Platform to filter by
 * @returns Posts for the specified platform
 */
export function getPostsByPlatform(
	posts: readonly ReadyToSharePostItem[],
	platform: PlatformType,
): ReadyToSharePostItem[] {
	return Array.from(posts).filter((post) => post.platform.toLowerCase() === platform);
}

/**
 * Converts API platform and postTypeName to FullPostType
 * @param platform - Platform from API
 * @param postTypeName - Post type name from API
 * @returns FullPostType for the PostPreview component
 */
export function getFullPostType(platform: PlatformType, postTypeName: string): FullPostType {
	const postTypeNameLower = postTypeName.toLowerCase();

	if (platform === 'facebook') {
		if (postTypeNameLower === 'story') {
			return 'facebook_story';
		}
		return 'facebook_post';
	}

	if (platform === 'facebookgroups') {
		return 'facebook_group_post';
	}

	if (platform === 'instagram') {
		if (postTypeNameLower === 'story') {
			return 'instagram_story';
		}
		return 'instagram_post';
	}

	// platform === 'reddit'
	return 'reddit_group_post';
}

/**
 * Groups posts from PlatformDataMap by platform type.
 */
export function groupPostsByPlatformData(platformData: PlatformDataMap): PlatformGroupedPosts {
	return {
		facebook: platformData.FACEBOOK?.items || [],
		instagram: platformData.INSTAGRAM?.items || [],
		reddit: platformData.REDDIT?.items || [],
		facebookgroups: platformData.FACEBOOKGROUPS?.items || [],
	};
}

/**
 * Gets loading states for each platform from PlatformDataMap.
 */
export function groupLoadingStatesByPlatform(platformData: PlatformDataMap): Record<PlatformType, boolean> {
	return {
		facebook: platformData.FACEBOOK?.status === 'PENDING',
		instagram: platformData.INSTAGRAM?.status === 'PENDING',
		reddit: platformData.REDDIT?.status === 'PENDING',
		facebookgroups: platformData.FACEBOOKGROUPS?.status === 'PENDING',
	};
}

/**
 * Determines which platforms have completed loading (terminal state) but failed to produce valid content.
 * These platforms need retry functionality.
 */
export function groupRetryStatesByPlatform(platformData: PlatformDataMap): Record<PlatformType, boolean> {
	const isTerminalState = (status: string | undefined) =>
		status === 'COMPLETE' || status === 'NOT_AVAILABLE' || status === 'FAILED';

	return {
		facebook: isTerminalState(platformData.FACEBOOK?.status),
		instagram: isTerminalState(platformData.INSTAGRAM?.status),
		reddit: isTerminalState(platformData.REDDIT?.status),
		facebookgroups: isTerminalState(platformData.FACEBOOKGROUPS?.status),
	};
}

/**
 * Checks if a group platform has the required groups to display content.
 * For facebookgroups, checks hasFacebookGroups; for reddit, checks hasRedditGroups.
 */
export function hasRequiredGroups(platform: PlatformType, recommendationsData: RecommendationsData): boolean {
	if (platform === 'facebookgroups') {
		return recommendationsData.hasFacebookGroups;
	}
	if (platform === 'reddit') {
		return recommendationsData.hasRedditGroups;
	}
	return true; // Non-group platforms don't require groups
}

/**
 * Computes which platforms should be available for display based on their content, loading, and retry states.
 */
export function computeAvailablePlatforms(
	validGroupedPosts: PlatformGroupedPosts,
	loadingStates: Record<PlatformType, boolean>,
	retryStates: Record<PlatformType, boolean>,
	recommendationsData: RecommendationsData,
): PlatformType[] {
	const platformNames = ALL_AVAILABLE_PLATFORMS.reduce<Set<PlatformType>>((acc, platform) => {
		const hasContent = validGroupedPosts[platform].length > 0;
		const isGroupPlatform = platform === 'facebookgroups' || platform === 'reddit';
		// Needs retry: terminal state AND no valid content
		const needsRetry = retryStates[platform] && !hasContent;

		// Platform is loading - include it
		if (loadingStates[platform]) {
			platform === 'facebookgroups' ? acc.add('facebook') : acc.add(platform);
			return acc;
		}

		// Platform has valid posts - include it
		if (hasContent) {
			platform === 'facebookgroups' ? acc.add('facebook') : acc.add(platform);
			return acc;
		}

		// Platform needs retry (terminal state with no content) - include it unless it's a group platform without groups
		if (needsRetry) {
			if (isGroupPlatform && !hasRequiredGroups(platform, recommendationsData)) {
				return acc;
			}
			platform === 'facebookgroups' ? acc.add('facebook') : acc.add(platform);
		}

		return acc;
	}, new Set<PlatformType>());

	return Array.from(platformNames);
}
